import React from "react";
import { Chip, Divider, TablePagination, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import { useNavigate } from "react-router-dom";
import { NotificationPlatformChips, usedApps } from "./getColumns";
import { resolveUserRoles } from "./userRoles";
import {
    SIGNUP_SOURCE_LABELS,
    isStandardOrPremiumPlan,
} from "../../services/PayPlanUtil";

function displayName(row) {
    const name = typeof row?.name === "string" ? row.name.trim() : "";
    return name || "";
}

function displayEmail(row) {
    const email = typeof row?.email === "string" ? row.email.trim() : "";
    return email || "";
}

function registeredLabel(value) {
    if (value == null || String(value).trim() === "") return "";
    const key = String(value).trim().toUpperCase();
    return SIGNUP_SOURCE_LABELS[key] || String(value);
}

function CardSection({ children }) {
    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {children}
        </Box>
    );
}

function AccountUserCard({ row, onOpen }) {
    const name = displayName(row);
    const email = displayEmail(row);
    const roles = resolveUserRoles(row).filter((role) => role !== "USER");
    const apps = usedApps(row);
    const registered = registeredLabel(row?.signupSource);
    const title = name || email || "User";

    return (
        <Card variant="outlined" sx={{ height: "100%" }}>
            <CardActionArea
                onClick={() => onOpen(row)}
                sx={{ height: "100%", alignItems: "stretch" }}
            >
                <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.25, py: 1.5 }}>
                    <CardSection>
                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3, wordBreak: "break-word" }}>
                                {title}
                            </Typography>
                            {name && email ? (
                                <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                                    {email}
                                </Typography>
                            ) : null}
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {roles.map((role) => (
                                <Chip
                                    key={role}
                                    label={role}
                                    size="small"
                                    variant="filled"
                                    color={role === "ADMIN" ? "error" : role === "TUTOR" ? "secondary" : "default"}
                                />
                            ))}
                            {registered ? (
                                <Chip label={`Sign up ${registered}`} size="small" variant="outlined" />
                            ) : null}
                            {row.stripeLivemode != null ? (
                                <Chip
                                    label={row.stripeLivemode ? "Live" : "Test"}
                                    color={row.stripeLivemode ? "success" : "default"}
                                    size="small"
                                    variant={row.stripeLivemode ? "filled" : "outlined"}
                                />
                            ) : null}
                        </Box>
                    </CardSection>
                    <Divider />
                    <CardSection>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                            <Chip
                                label={`Enabled ${row.enabled ? "Yes" : "No"}`}
                                size="small"
                                color={row.enabled ? "success" : "default"}
                                variant={row.enabled ? "filled" : "outlined"}
                            />
                            <Chip
                                label={`Email ${row.emailVerified ? "Yes" : "No"}`}
                                size="small"
                                color={row.emailVerified ? "success" : "default"}
                                variant={row.emailVerified ? "filled" : "outlined"}
                            />
                        </Box>
                    </CardSection>
                    <Divider />
                    <CardSection>
                        <NotificationPlatformChips row={row} />
                    </CardSection>
                    <Divider />
                    <CardSection>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {apps.length === 0 ? (
                                <Typography variant="caption" color="text.secondary">
                                    No app purchase or plays
                                </Typography>
                            ) : (
                                apps.map((app) => {
                                    const paid = isStandardOrPremiumPlan(app.plan);
                                    const playBit = app.countPlay > 0 ? ` ${app.countPlay}` : "";
                                    const planBit = paid ? ` ${app.plan}` : "";
                                    return (
                                        <Chip
                                            key={app.appCode}
                                            label={`${app.label}${planBit}${playBit}`}
                                            size="small"
                                            variant={paid ? "filled" : "outlined"}
                                            color="secondary"
                                        />
                                    );
                                })
                            )}
                        </Box>
                    </CardSection>
                    {row.createdAt && row.createdAt !== "N/A" ? (
                        <Typography variant="caption" color="text.secondary">
                            Created {row.createdAt}
                        </Typography>
                    ) : null}
                    <Typography variant="caption" color="text.secondary">
                        Last login {row.lastLoggedInAt || "—"}
                    </Typography>
                </CardContent>
            </CardActionArea>
        </Card>
    );
}

export default function AccountCardGrid({ rows, rowCount, pageModel, onPageModelChange }) {
    const navigate = useNavigate();

    const openUser = (row) => {
        if (!row?.id) return;
        navigate(`/admin/account/subscription/${row.id}`);
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            {rows.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
                    No users found.
                </Typography>
            ) : (
                <Grid container spacing={2}>
                    {rows.map((row) => (
                        <Grid key={row.id} size={{ xs: 12, sm: 6, md: 6, lg: 4 }}>
                            <AccountUserCard row={row} onOpen={openUser} />
                        </Grid>
                    ))}
                </Grid>
            )}
            <TablePagination
                component="div"
                count={rowCount}
                page={pageModel.page}
                onPageChange={(_event, page) => onPageModelChange({ ...pageModel, page })}
                rowsPerPage={pageModel.pageSize}
                onRowsPerPageChange={(event) =>
                    onPageModelChange({
                        page: 0,
                        pageSize: Number(event.target.value),
                    })
                }
                rowsPerPageOptions={[25, 50, 100]}
            />
        </Box>
    );
}
